/**
 * Render layer and map-level features on a grid and map and manage viewport changes
 * @module render-features
 * @see {@link http://wiki.infor.com:8080/confluence/label/~CALIX/render-features|blog}
 */

import _ = require("underscore");
import ol = require("ol");
import Mn = require("backbone.marionette");
import MapView = require("../views/MapView");
import Utils = require("../utils");
import Portal = require("portal-gis");
import Mediator = require("../mediator");
import Base = require("./base-command");
import defaults = require("../models/defaults");
import Converter = require("../utils/ol3/converter");
import geo = require("../utils/geo");
import Controller = require("../controls/controller");
const log = Utils.log;

// multiple controllers may invoke this handler concurrently, delay rendering until results are collected
type DelayedRenders = Rhythm.GIS.Dictionary<{
    features: Array<{ geometry: any }>;
    extent: number[];
    render: () => void;
}>;

function scaleFromCenter(extent: ol.Extent, value: number) {
    var deltaX = ((extent[2] - extent[0]) / 2) * (value - 1);
    var deltaY = ((extent[3] - extent[1]) / 2) * (value - 1);
    extent[0] -= deltaX;
    extent[2] += deltaX;
    extent[1] -= deltaY;
    extent[3] += deltaY;
};

class Command extends Base<{
    in: any;
    out: {
        layers: Array<{
            spatialReference?: { wkid: string };
            features: Rhythm.GIS.FeatureQueryResultFeature[];
        }>
    };
}> {

    static about = `
Zooms the map around a collection of features unless zoommap = "false", in which case it only re-centers the map about the features
Pans the map to the center of the features unless panmap = "false", in which case it only grows the current extent to include the new features
The collection of features includes all features ever seen by this command unless preserve-extent = "false", in which case it only considers the current features.

Example Usage:

    <EVENT event="auto-zoom,liferay:auto-zoom" id="auto-zoom-handler" mid="app/commands/auto-zoom">
    <OPTIONS>
        <Values id="panmap" value="true" />
        <Values id="zoommap" value="true" />
        <Values about="true =&gt; Prevent the current viewport to pan out of view" id="preserve-extent" value="false" />
    </OPTIONS>
    </EVENT>

`;

    static options = {
        "panmap": {
            "about": "false to prevent panning",
            "default": "true",
        },
        "preserve-extent": {
            "about": "true to prevent current extent from being clipped",
            "default": "true",
        },
        "zoommap": {
            "about": "false to prevent zooming",
            "default": "true",
        },
    }

    delayedRender() {
        let modelId = `commands:auto-zoom:delayed-renderers`;
        let renderers = <DelayedRenders>this.mediator.model.get(modelId);
        if (!renderers) {
            renderers = {};
            this.mediator.model.set(modelId, renderers);
        }
        let renderer = renderers[this.getId()];
        if (!renderer) {
            renderer = {
                features: [],
                extent: null,
                render: _.debounce(() => this.updateViewport(), 100)
            };
            renderers[this.getId()] = renderer;
        }
        return renderer;
    }

    execute() {
        let options = this.asOptions(Command.options);
        
        let panmap = this.options.panmap !== "false";
        let zoommap = this.options.zoommap !== "false";
        if (!panmap && !zoommap) return;

        let delayedRender = this.delayedRender();

        // all features coming through this command should appear in the viewport
        this.mediator.on("clear-features-from-layer", (args: {
            layer: string;
        }) => {
            delayedRender.extent = null;
        });

        // start the clock to render these features
        delayedRender.render();

        let layers = this.args.out.layers || this.findOption("layers");
        layers.forEach(l => {
            let features = l.features.map(f => ({
                geometry: f.geometry,
                geometryType: f.geometryType
            }));

            if (!l.spatialReference) {
                log("setting SRS explicitly");
                l.spatialReference = { wkid: "EPSG:4326" };
            } else {
                // reproject to 4326
                let transformer = geo.transformer(l.spatialReference.wkid, "EPSG:4326");
                features.forEach(f => {
                    switch (f.geometryType) {
                        case "point":
                            f.geometry = transformer.point(f.geometry);
                            break;
                        case "multiline":
                            f.geometry = transformer.points(f.geometry);
                            break;
                        case "polygon":
                            f.geometry = transformer.paths(f.geometry);
                            break;
                        case "multipoint":
                            f.geometry = transformer.points(f.geometry);
                            break;
                        default:
                            console.debug("unknown geometry type:", f.geometryType);
                            break;
                    }
                });
            }
            delayedRender.features = delayedRender.features.concat(features);
        });
        this.trigger();
    }

    private getId() {
        return this.configuration.id;
    }

    private updateViewport() {
        log("updating viewport for controller: ", this.getId());

        let options = this.asOptions(Command.options);
        let panmap = options.panmap !== "false";
        let zoommap = options.zoommap !== "false";
        if (!panmap && !zoommap) return;

        let preserveExtent = options["preserve-extent"] !== "false";

        let delayedRender = this.delayedRender();
        let extent = delayedRender.extent;

        this.mediator.mapView(mapView => {
            this.mediator.mapViewMap(map => {
                let features = delayedRender.features.filter(f => !!f.geometry);
                // IPS may have  invalid X/Y data, which will corrupt this extent but
                // we have no way of knowing the SRS and X/Y our mismatched so there's no way of fixing this in general
                // so instead we just don't set the extent if it is invalid see YAH-001
                features.forEach(f => extent = geo.grow(extent, f.geometry));

                // clear the features
                delayedRender.features = [];

                if (extent) {
                    let currentExtent = mapView.extent.geometry;
                    if (!panmap) {
                        // merge the extents
                        extent = geo.grow(extent, [currentExtent[0], currentExtent[1]]);
                        extent = geo.grow(extent, [currentExtent[2], currentExtent[3]]);
                    }
                    else if (!zoommap) {
                        // change the center point only
                        let [x, y] = [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];
                        let [w, h] = [currentExtent[2] - currentExtent[0], currentExtent[3] - currentExtent[1]];
                        extent = [x - w / 2, y - h / 2, x + w / 2, y + h / 2];
                    } else {
                        // panmap and zoommap
                        let [x, y] = [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];
                        let [cw, ch] = [currentExtent[2] - currentExtent[0], currentExtent[3] - currentExtent[1]];
                        let [nw, nh] = [extent[2] - extent[0], extent[3] - extent[1]];
                        let [w, h] = [nw, nh];
                        extent = [x - w / 2, y - h / 2, x + w / 2, y + h / 2];
                    }

                    // reset the internal extent
                    delayedRender.extent = null;

                    //YAH-001: only set the extent if it is valid
                    let mapExtent = geo.asExtent(extent.join(","));
                    if (!mapExtent.geometry.some(v => isNaN(v))) {
                        // preserve the extent
                        if (preserveExtent) delayedRender.extent = extent.map(v => v);
                        // provides a margin around the final extent to ensure no markers are completely off the map
                        scaleFromCenter(mapExtent.geometry, 1 + parseInt(this.findOption("margin") || "20") / 100);
                        mapView.extent = mapExtent;
                        this.trigger({ extent: extent });
                    }

                }
            });
        });
    }

}

export = Command;